function asObject(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function pickNodeType(node = {}) {
    const data = asObject(node.data);
    return String(data.nodeType || data.type || node.name || "").trim();
}

function pickNodeLabel(node = {}) {
    const data = asObject(node.data);
    return String(
        data.label ||
        data.name ||
        data.conditionText ||
        data.errorCode ||
        node.name ||
        ""
    ).trim();
}

function normalizeNodeData(data = {}) {
    const source = asObject(data);
    const out = {};
    for (const [key, value] of Object.entries(source)) {
        if (value === "" || value == null) continue;
        out[key] = value;
    }
    return out;
}

function buildNodeRecord(moduleName, node = {}) {
    const data = normalizeNodeData(node.data);
    const id = String(node.id ?? "").trim();
    const type = pickNodeType(node);
    const outputs = Object.keys(asObject(node.outputs));
    const inputs = Object.keys(asObject(node.inputs));
    return {
        nodeRef: `${moduleName}:${id}`,
        module: moduleName,
        id,
        name: String(node.name || "").trim(),
        type,
        label: pickNodeLabel(node),
        signalName: String(data.signalName || "").trim(),
        mode: String(data.mode || "").trim(),
        action: String(data.action || "").trim(),
        conditionText: String(data.conditionText || "").trim(),
        trueLabel: String(data.trueLabel || "").trim(),
        falseLabel: String(data.falseLabel || "").trim(),
        timeoutLabel: String(data.timeoutLabel || "").trim(),
        inputs,
        outputs,
        data
    };
}

function buildConnectionRecord(moduleName, fromNode = {}, outputName, connection = {}) {
    const fromId = String(fromNode.id ?? "").trim();
    const toId = String(connection.node ?? connection.to ?? "").trim();
    const inputName = String(connection.output || connection.input || "input_1").trim();
    const output = String(outputName || "output_1").trim();
    return {
        module: moduleName,
        from: fromId,
        to: toId,
        fromRef: `${moduleName}:${fromId}`,
        toRef: `${moduleName}:${toId}`,
        output,
        input: inputName
    };
}

export function buildRawFlowIndex(drawflowSnapshot = {}) {
    const root = asObject(drawflowSnapshot.drawflow);
    const nodes = [];
    const connections = [];

    for (const [moduleName, moduleObj] of Object.entries(root)) {
        const moduleData = asObject(moduleObj?.data);
        for (const node of Object.values(moduleData)) {
            const record = buildNodeRecord(moduleName, node);
            if (!record.id) continue;
            nodes.push(record);

            for (const [outputName, output] of Object.entries(asObject(node.outputs))) {
                const outputConnections = Array.isArray(output?.connections) ? output.connections : [];
                for (const connection of outputConnections) {
                    if (!connection) continue;
                    connections.push(buildConnectionRecord(moduleName, node, outputName, connection));
                }
            }
        }
    }

    return {
        contractVersion: "1.0",
        modules: Object.keys(root),
        nodes,
        connections
    };
}

export function buildRawFlowIndexFromFlowContext(flowContext = {}) {
    const nodes = [];
    const connections = [];
    const modules = [];
    const pushNodes = (moduleName, flowNodes = [], moduleConnections = []) => {
        if (!modules.includes(moduleName)) modules.push(moduleName);
        for (const node of Array.isArray(flowNodes) ? flowNodes : []) {
            const data = normalizeNodeData({
                label: node.label,
                signalName: node.signalName,
                action: node.action,
                conditionText: node.conditionText,
                trueLabel: node.trueLabel,
                falseLabel: node.falseLabel,
                timeoutLabel: node.timeoutLabel
            });
            nodes.push({
                nodeRef: `${moduleName}:${String(node.id ?? "").trim()}`,
                module: moduleName,
                id: String(node.id ?? "").trim(),
                name: String(node.name || "").trim(),
                type: String(node.type || "").trim(),
                label: String(node.label || node.name || node.conditionText || "").trim(),
                signalName: String(node.signalName || "").trim(),
                mode: String(node.mode || "").trim(),
                action: String(node.action || "").trim(),
                conditionText: String(node.conditionText || "").trim(),
                trueLabel: String(node.trueLabel || "").trim(),
                falseLabel: String(node.falseLabel || "").trim(),
                timeoutLabel: String(node.timeoutLabel || "").trim(),
                inputs: Array.isArray(node.inputs) ? node.inputs : [],
                outputs: Array.isArray(node.outputs) ? node.outputs : [],
                data
            });
        }
        for (const connection of Array.isArray(moduleConnections) ? moduleConnections : []) {
            const fromId = String(connection.from ?? "").trim();
            const toId = String(connection.to ?? "").trim();
            if (!fromId || !toId) continue;
            connections.push({
                module: moduleName,
                from: fromId,
                to: toId,
                fromRef: `${moduleName}:${fromId}`,
                toRef: `${moduleName}:${toId}`,
                output: String(connection.output || "output_1").trim(),
                input: String(connection.input || "input_1").trim()
            });
        }
    };

    pushNodes("Home", flowContext.flowNodes, flowContext.connections);
    for (const moduleItem of Array.isArray(flowContext.modules) ? flowContext.modules : []) {
        pushNodes(
            String(moduleItem?.moduleName || "").trim() || "Module",
            moduleItem?.flowNodes,
            moduleItem?.connections
        );
    }

    return {
        contractVersion: "1.0",
        modules,
        nodes,
        connections
    };
}

function findNodeMatches(rawFlowIndex, nodeRefOrId = "") {
    const needle = String(nodeRefOrId || "").trim();
    if (!needle) return [];
    const nodes = Array.isArray(rawFlowIndex?.nodes) ? rawFlowIndex.nodes : [];
    const exactRef = nodes.filter((node) => node.nodeRef === needle);
    if (exactRef.length > 0) return exactRef;
    return nodes.filter((node) => node.id === needle);
}

export function getFlowNode(rawFlowIndex, nodeRefOrId = "") {
    return findNodeMatches(rawFlowIndex, nodeRefOrId)[0] || null;
}

export function searchFlowNodes(rawFlowIndex, query = "", limit = 8) {
    const needle = String(query || "").trim().toLowerCase();
    if (!needle) return [];
    const terms = needle.split(/\s+/).filter(Boolean);
    const maxResults = Math.max(1, Number(limit) || 8);
    const nodes = Array.isArray(rawFlowIndex?.nodes) ? rawFlowIndex.nodes : [];
    return nodes
        .filter((node) => {
            const haystack = [
                node.nodeRef,
                node.module,
                node.id,
                node.type,
                node.label,
                node.name,
                node.signalName,
                node.mode,
                node.action,
                node.conditionText,
                JSON.stringify(node.data || {})
            ].join(" ").toLowerCase();
            return terms.every((term) => haystack.includes(term));
        })
        .slice(0, maxResults);
}

export function getFlowNeighborhood(rawFlowIndex, nodeRefOrId = "", depth = 1) {
    const centerNode = getFlowNode(rawFlowIndex, nodeRefOrId);
    if (!centerNode) return null;

    const maxDepth = Math.max(1, Math.min(3, Number(depth) || 1));
    const nodesByRef = new Map(
        (Array.isArray(rawFlowIndex?.nodes) ? rawFlowIndex.nodes : []).map((node) => [node.nodeRef, node])
    );
    const connections = (Array.isArray(rawFlowIndex?.connections) ? rawFlowIndex.connections : [])
        .filter((connection) => connection.module === centerNode.module);

    const visited = new Set([centerNode.nodeRef]);
    const queue = [{ nodeRef: centerNode.nodeRef, depth: 0 }];
    const neighborhoodConnections = [];

    while (queue.length > 0) {
        const current = queue.shift();
        if (!current || current.depth >= maxDepth) continue;

        for (const connection of connections) {
            if (connection.fromRef !== current.nodeRef && connection.toRef !== current.nodeRef) continue;
            neighborhoodConnections.push(connection);

            const nextRef = connection.fromRef === current.nodeRef ? connection.toRef : connection.fromRef;
            if (!nodesByRef.has(nextRef) || visited.has(nextRef)) continue;
            visited.add(nextRef);
            queue.push({ nodeRef: nextRef, depth: current.depth + 1 });
        }
    }

    return {
        centerNodeRef: centerNode.nodeRef,
        module: centerNode.module,
        depth: maxDepth,
        nodes: [...visited].map((nodeRef) => nodesByRef.get(nodeRef)).filter(Boolean),
        connections: neighborhoodConnections.filter((connection, index, list) => {
            const key = `${connection.fromRef}:${connection.output}:${connection.toRef}:${connection.input}`;
            return list.findIndex((item) => `${item.fromRef}:${item.output}:${item.toRef}:${item.input}` === key) === index;
        })
    };
}
